import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, Text, View } from "react-native";
import Markdown from "react-native-markdown-display";
import Toast from "react-native-toast-message";
import ClockIcon from "@/assets/svgs/clock-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import SubjectIcon from "@/assets/svgs/subject-icon";
import UserIcon from "@/assets/svgs/user-icon";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import Loader from "../shared/loader";
import ResponsiveContent from "../shared/responsive-content";
import { AppBar } from "../ui/app-bar";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import { Tab } from "../ui/tab";
import { Textarea } from "../ui/textarea";
import { api } from "../utils/convex-api";
import goBack from "../utils/go-back";
import LessonReportWellbeingTab from "./lesson-report-wellbeing-tab";
import StudyMaterialList from "./study-material-list";
import {
  formatWellbeingSections,
  parseWellbeingSections,
  type WellbeingSection,
} from "./wellbeing-insights-sections";

const markdownStyles = {
  body: { fontFamily: "Geologica_400Regular", color: "#181718" },
  heading1: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading2: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading3: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading4: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading5: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading6: { fontFamily: "Geologica_500Medium", color: "#181718" },
  text: { color: "#181718" },
  strong: { fontFamily: "Geologica_500Medium", color: "#181718" },
};

function TutorLessonReportTab({
  initialData,
  inReview,
  lessonReportId,
  onNextTab,
}: {
  initialData: {
    topicsCovered: string | null | undefined;
    strengths: string | null | undefined;
    challenges: string | null | undefined;
    studentComments: string | null | undefined;
    tutorComments: string | null | undefined;
  };
  inReview: boolean;
  lessonReportId: Id<"lessonReports">;
  onNextTab: () => void;
}) {
  const [reportEditable, setReportEditable] = useState(false);
  const [topicsCovered, setTopicsCovered] = useState(
    initialData.topicsCovered ?? "",
  );
  const [strengths, setStrengths] = useState(initialData.strengths ?? "");
  const [challenges, setChallenges] = useState(initialData.challenges ?? "");
  const studentComments = initialData.studentComments ?? "";
  const [tutorComments, setTutorComments] = useState(
    initialData.tutorComments ?? "",
  );
  const { mutateAsync: updateLessonReport } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.lessonReports.mutations.updateLessonReport,
    ),
  });

  return (
    <View className="gap-6">
      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Topics covered
          </Text>

          {!reportEditable && (
            <Text
              onPress={() => setReportEditable(true)}
              className="text-base font-geologica-medium font-medium text-teal-600"
            >
              Edit
            </Text>
          )}
        </View>
        {reportEditable ? (
          <Textarea
            value={topicsCovered}
            onChangeText={setTopicsCovered}
            style={{
              height: 139,
            }}
            multiline
          />
        ) : (
          <View className="py-3">
            <Markdown style={markdownStyles}>{topicsCovered}</Markdown>
          </View>
        )}
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Strengths
          </Text>
        </View>
        {reportEditable ? (
          <Textarea
            value={strengths}
            onChangeText={setStrengths}
            style={{
              height: 139,
            }}
            multiline
          />
        ) : (
          <View className="py-3">
            <Markdown style={markdownStyles}>{strengths}</Markdown>
          </View>
        )}
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Challenges
          </Text>
        </View>
        {reportEditable ? (
          <Textarea
            value={challenges}
            onChangeText={setChallenges}
            style={{
              height: 139,
            }}
            multiline
          />
        ) : (
          <View className="py-3">
            <Markdown style={markdownStyles}>{challenges}</Markdown>
          </View>
        )}
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Student comments
          </Text>
        </View>

        <View className="py-3">
          <Markdown style={markdownStyles}>{studentComments}</Markdown>
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Tutor comments
          </Text>
        </View>
        {reportEditable ? (
          <Textarea
            value={tutorComments}
            onChangeText={setTutorComments}
            style={{
              height: 139,
            }}
            multiline
          />
        ) : (
          <View className="py-3">
            <Markdown style={markdownStyles}>{tutorComments}</Markdown>
          </View>
        )}
      </View>

      {(reportEditable || inReview) && (
        <View className="mx-10">
          <Button
            size="sm"
            outline
            fullWidth
            onPress={async () => {
              setReportEditable(false);
              if (inReview) {
                await updateLessonReport({
                  lessonReportId: lessonReportId,
                  topics: topicsCovered,
                  strengths: strengths,
                  challenges: challenges,
                  tutorComment: tutorComments,
                });
                onNextTab();
              }
            }}
          >
            <Button.Text size="sm" intent="primary">
              {inReview ? "Next" : "Save changes"}
            </Button.Text>
          </Button>
        </View>
      )}
    </View>
  );
}

function TutorLessonReportNextStepsTab({
  initialData,
  inReview,
  lessonReportId,
  onNextTab,
}: {
  initialData: { nextSteps: string | null | undefined };
  inReview: boolean;
  lessonReportId: Id<"lessonReports">;
  onNextTab: () => void;
}) {
  const [nextStepsEditable, setNextStepsEditable] = useState(false);
  const [nextSteps, setNextSteps] = useState(initialData.nextSteps ?? "");
  const { mutateAsync: updateLessonReport } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.lessonReports.mutations.updateLessonReport,
    ),
  });

  return (
    <View className="gap-6">
      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Next steps
          </Text>

          {!nextStepsEditable && (
            <Text
              onPress={() => setNextStepsEditable(true)}
              className="text-base font-geologica-medium font-medium text-teal-600"
            >
              Edit
            </Text>
          )}
        </View>
        {nextStepsEditable ? (
          <Textarea
            value={nextSteps}
            onChangeText={setNextSteps}
            style={{
              height: 139,
            }}
            multiline
          />
        ) : (
          <View className="py-3">
            <Markdown style={markdownStyles}>{nextSteps}</Markdown>
          </View>
        )}
      </View>

      {(nextStepsEditable || inReview) && (
        <View className="mx-10">
          <Button
            size="sm"
            outline
            fullWidth
            onPress={async () => {
              setNextStepsEditable(false);
              if (inReview) {
                await updateLessonReport({
                  lessonReportId: lessonReportId,
                  nextSteps,
                });
                onNextTab();
              }
            }}
          >
            <Button.Text size="sm" intent="primary">
              {inReview ? "Next" : "Save changes"}
            </Button.Text>
          </Button>
        </View>
      )}
    </View>
  );
}

function TutorLessonReportWellbeingTab({
  initialData,
  inReview,
  lessonReportId,
  onNext,
}: {
  initialData: {
    aiWellbeingReport: string | null | undefined;
    studentMood: string | null | undefined;
    studentMoodComment: string | null | undefined;
    studentComment: string | null | undefined;
    studentName: string | null | undefined;
  };
  inReview: boolean;
  lessonReportId: Id<"lessonReports">;
  onNext: () => void;
}) {
  const report = initialData.aiWellbeingReport ?? "";

  const [editable, setEditable] = useState(false);
  const [sections, setSections] = useState<WellbeingSection[]>(() =>
    parseWellbeingSections(report),
  );
  const [freeform, setFreeform] = useState(report);
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: updateLessonReport } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.lessonReports.mutations.updateLessonReport,
    ),
  });

  const hasSections = sections.length > 0;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateLessonReport({
        lessonReportId,
        aiWellbeingReport: hasSections
          ? formatWellbeingSections(sections)
          : freeform,
      });
      setEditable(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (editable) {
    return (
      <View className="gap-6">
        {hasSections ? (
          sections.map((section, index) => (
            <View key={section.title} className="gap-2">
              <Text className="text-base font-geologica-medium font-medium text-gray-800">
                {section.title}
              </Text>
              <Textarea
                value={section.content}
                onChangeText={(text) =>
                  setSections((previous) =>
                    previous.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, content: text } : item,
                    ),
                  )
                }
                style={{
                  height: 139,
                }}
                multiline
              />
            </View>
          ))
        ) : (
          <View className="gap-2">
            <Text className="text-base font-geologica-medium font-medium text-gray-800">
              Wellbeing insights
            </Text>
            <Textarea
              value={freeform}
              onChangeText={setFreeform}
              style={{
                height: 139,
              }}
              multiline
            />
          </View>
        )}

        <View className="mx-10">
          <Button
            size="sm"
            outline
            fullWidth
            disabled={isSaving}
            onPress={handleSave}
          >
            <Button.Text size="sm" intent="primary">
              {isSaving ? "Saving..." : "Save changes"}
            </Button.Text>
          </Button>
        </View>
      </View>
    );
  }

  return (
    <View className="gap-2">
      {inReview && (
        <View className="flex-row items-center justify-end">
          <Text
            onPress={() => setEditable(true)}
            className="text-base font-geologica-medium font-medium text-teal-600"
          >
            Edit
          </Text>
        </View>
      )}

      <LessonReportWellbeingTab
        initialData={initialData}
        inReview={inReview}
        onNext={onNext}
      />
    </View>
  );
}

function RetryTranscriptionButton({
  isRetrying,
  onRetry,
}: {
  isRetrying: boolean;
  onRetry: () => void;
}) {
  return (
    <View className="mx-10">
      <Button
        size="sm"
        outline
        fullWidth
        disabled={isRetrying}
        onPress={onRetry}
      >
        {isRetrying ? (
          <ActivityIndicator size="small" color="#2C7A7B" />
        ) : (
          <Button.Text size="sm" intent="primary">
            Retry transcription
          </Button.Text>
        )}
      </Button>
    </View>
  );
}

function TutorLessonReport() {
  const fmt = useFormatDate();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<
    "report" | "next-steps" | "well-being"
  >("report");
  const [isRetrying, setIsRetrying] = useState(false);
  const prevTopicsRef = useRef<string | null | undefined>(undefined);
  const { lessonReportId } = useLocalSearchParams<{
    lessonReportId?: Id<"lessonReports">;
  }>();
  const { data: lessonReport, isLoading } = useQuery(
    convexQuery(
      api.tutor.lessonHistory.queries.getLessonReport,
      lessonReportId
        ? {
            lessonReportId: lessonReportId,
          }
        : "skip",
    ),
  );
  const { mutateAsync: retryTranscription } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.lessonReports.mutations.retryTranscription,
    ),
  });

  useEffect(() => {
    if (!isRetrying) return;
    if (lessonReport?.topics === prevTopicsRef.current) return;

    setIsRetrying(false);
    if (
      lessonReport?.topics &&
      !lessonReport.topics.startsWith("No recording available")
    ) {
      Toast.show({ type: "success", text1: "Transcription updated" });
    } else {
      Toast.show({
        type: "error",
        text1: "Transcription not available yet",
        text2: "Please try again later",
      });
    }
  }, [lessonReport?.topics, isRetrying]);

  useEffect(() => {
    if (!isRetrying) return;
    const timer = setTimeout(() => {
      setIsRetrying(false);
      Toast.show({
        type: "error",
        text1: "Transcription not available yet",
        text2: "Please try again later",
      });
    }, 10000);
    return () => clearTimeout(timer);
  }, [isRetrying]);

  const handleRetryTranscription = async () => {
    if (!lessonReportId) return;
    prevTopicsRef.current = lessonReport?.topics;
    setIsRetrying(true);
    try {
      await retryTranscription({ lessonReportId });
    } catch {
      setIsRetrying(false);
      Toast.show({ type: "error", text1: "Failed to retry transcription" });
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!lessonReportId) {
    return <Redirect href="/" />;
  }

  if (
    lessonReport?.status === "in_progress" ||
    lessonReport?.status === "pending"
  ) {
    return (
      <Fragment>
        <AppBar>
          <AppBar.Icon onPress={goBack} />
          <AppBar.Title>Back</AppBar.Title>
        </AppBar>
        <View className="flex-1 justify-center items-center gap-4 px-5">
          <SubjectIcon
            width={48}
            height={48}
            pathClassName="stroke-gray-400 stroke-2"
          />
          <Text className="text-xl font-geologica-medium font-medium text-gray-800 text-center">
            Processing lesson report...
          </Text>
          <Text className="text-base font-geologica-regular font-normal text-gray-500 text-center">
            This may take a few moments. The page will update automatically when
            ready.
          </Text>
        </View>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-2" />
            <View className="gap-8">
              {lessonReport?.status === "pending_review" ? (
                <View className="gap-1">
                  <Text className="text-[32px] text-gray-800 leading-[1.2] font-medium font-zilla-slab-medium">
                    Review lesson report
                  </Text>
                  <Text className="text-xl font-geologica-regular font-normal leading-[1.5] text-gray-800">
                    {lessonReport?.startedAt
                      ? fmt(lessonReport?.startedAt, "EEE, do MMMM")
                      : ""}
                  </Text>
                </View>
              ) : (
                <View className="gap-1">
                  <Text className="text-[32px] text-gray-800 leading-[1.2] font-medium font-zilla-slab-medium">
                    Lesson report
                  </Text>
                  <Text className="text-xl font-geologica-regular font-normal leading-[1.5] text-gray-800">
                    {lessonReport?.completedAt
                      ? fmt(lessonReport?.completedAt, "EEE, do MMMM")
                      : ""}
                  </Text>
                </View>
              )}

              <View className="h-[1px] bg-gray-200" />

              <View className="flex-row justify-between items-center">
                <View className="flex-row gap-2">
                  <Text className="text-base font-geologica-medium font-medium text-gray-800">
                    Student
                  </Text>
                  <Avatar size="sm">
                    <Avatar.Content
                      labelSize="sm"
                      size="sm"
                      label={lessonReport?.student?.name}
                      source={
                        lessonReport?.student?.imageUrl
                          ? { uri: lessonReport?.student?.imageUrl }
                          : undefined
                      }
                    />
                  </Avatar>
                  <Text className="text-base font-geologica-regular font-normal text-gray-800">
                    {lessonReport?.student?.name}
                  </Text>
                </View>
                <View className="flex-row gap-2">
                  <Text className="text-base font-geologica-medium font-medium text-gray-800">
                    Tutor
                  </Text>
                  <Avatar size="sm">
                    <Avatar.Content
                      labelSize="sm"
                      size="sm"
                      label={lessonReport?.tutor?.name}
                      source={
                        lessonReport?.tutor?.imageUrl
                          ? { uri: lessonReport?.tutor?.imageUrl }
                          : undefined
                      }
                    />
                  </Avatar>
                  <Text className="text-base font-geologica-regular font-normal text-gray-800">
                    {lessonReport?.tutor?.name}
                  </Text>
                </View>
              </View>

              <View className="flex-row">
                <View className="flex-1">
                  <View className="flex-row items-center gap-2">
                    <SubjectIcon
                      className="size-6"
                      pathClassName={"stroke-gray-800"}
                    />
                    <Text
                      className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                    >
                      Subject
                    </Text>
                  </View>
                  <Text
                    className={`font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]`}
                  >
                    {lessonReport?.session?.subject}
                  </Text>
                </View>
                <View className="flex-1">
                  <View className="flex-row items-center gap-2">
                    {lessonReport?.session?.deliveryMethod === "in_person" ? (
                      <UserIcon
                        className="size-6"
                        pathClassName={"stroke-gray-800"}
                      />
                    ) : (
                      <RemoteIcon
                        className="size-6"
                        pathClassName={"stroke-gray-800"}
                      />
                    )}
                    <Text
                      className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                    >
                      Session
                    </Text>
                  </View>
                  <Text
                    className={`font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]`}
                  >
                    {lessonReport?.session?.deliveryMethod === "in_person"
                      ? "In person"
                      : "Remote"}
                  </Text>
                </View>
                <View className="flex-1">
                  <View className="flex-row items-center gap-2">
                    <ClockIcon
                      className="size-6"
                      pathClassName={"stroke-gray-800"}
                    />
                    <Text
                      className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                    >
                      Time
                    </Text>
                  </View>
                  {!!lessonReport?.session?.startTime &&
                    !!lessonReport?.session?.endTime && (
                      <Text
                        className={`font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]`}
                      >
                        {fmt(lessonReport?.session?.startTime, "HH:mm")} -{" "}
                        {fmt(lessonReport?.session?.endTime, "HH:mm")}
                      </Text>
                    )}
                </View>
              </View>

              {lessonReport?.status === "completed" && (
                <Fragment>
                  <View className="h-[1px] bg-gray-200" />

                  <StudyMaterialList files={lessonReport?.files ?? []} />
                </Fragment>
              )}

              <View className="h-[1px] bg-gray-200" />

              {(isRetrying ||
                (lessonReport?.status === "pending_review" &&
                  lessonReport?.topics?.startsWith(
                    "No recording available",
                  ))) && (
                <RetryTranscriptionButton
                  isRetrying={isRetrying}
                  onRetry={handleRetryTranscription}
                />
              )}

              <View className="flex-row gap-2">
                <Tab
                  size="md"
                  intent={activeTab === "report" ? "active" : "inActive"}
                  onPress={() => setActiveTab("report")}
                >
                  <Tab.Text>Report</Tab.Text>
                </Tab>
                <Tab
                  size="md"
                  intent={activeTab === "next-steps" ? "active" : "inActive"}
                  onPress={() => setActiveTab("next-steps")}
                >
                  <Tab.Text>Next steps</Tab.Text>
                </Tab>
                <Tab
                  size="md"
                  intent={activeTab === "well-being" ? "active" : "inActive"}
                  onPress={() => setActiveTab("well-being")}
                >
                  <Tab.Text>Well-being</Tab.Text>
                </Tab>
              </View>

              {activeTab === "report" && (
                <TutorLessonReportTab
                  key={lessonReport?.topics ?? ""}
                  initialData={{
                    topicsCovered: lessonReport?.topics,
                    strengths: lessonReport?.strengths,
                    challenges: lessonReport?.challenges,
                    studentComments: lessonReport?.studentComment,
                    tutorComments: lessonReport?.tutorComment,
                  }}
                  inReview={lessonReport?.status === "pending_review"}
                  lessonReportId={lessonReportId}
                  onNextTab={() => {
                    setActiveTab("next-steps");
                  }}
                />
              )}
              {activeTab === "next-steps" && (
                <TutorLessonReportNextStepsTab
                  key={lessonReport?.nextSteps ?? ""}
                  initialData={{
                    nextSteps: lessonReport?.nextSteps,
                  }}
                  inReview={lessonReport?.status === "pending_review"}
                  lessonReportId={lessonReportId}
                  onNextTab={() => {
                    setActiveTab("well-being");
                  }}
                />
              )}
              {activeTab === "well-being" && (
                <TutorLessonReportWellbeingTab
                  key={lessonReport?.aiWellbeingReport ?? ""}
                  initialData={{
                    aiWellbeingReport: lessonReport?.aiWellbeingReport,
                    studentMood: lessonReport?.studentMood,
                    studentMoodComment: lessonReport?.studentMoodComment,
                    studentComment: lessonReport?.studentComment,
                    studentName: lessonReport?.student?.name,
                  }}
                  inReview={lessonReport?.status === "pending_review"}
                  lessonReportId={lessonReportId}
                  onNext={() => {
                    router.push(
                      `/lesson-history/${lessonReportId}/study-materials`,
                    );
                  }}
                />
              )}
            </View>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </Fragment>
  );
}

export default TutorLessonReport;
